import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import {
    Card,
    CardContent,
    Collapse,
    Divider,
    Grid,
    IconButton,
    SvgIcon,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import {
    ResultadoDetalhado,
    ResultadoDetalhadoNota,
    ResultadoDetalhadoSacado,
    ResultadoDetalhadoTitulo,
    ShowResultadoDetalhado
} from '../../bordero.types'
import ResultadoDetalhadoAnaliseScope from '../resultado_detalhado_analise.scopes'

import {
    Business,
    CheckCircle,
    Description,
    ErrorOutlined as ErrorOutline,
    FileCopy,
    Groups,
    WarningAmber,
    Work
} from '@mui/icons-material'

import { formatDate } from 'date-fns'
import { formatCNPJ, formatCPFOrCNPJ, formatCurrency, formatDateNoTz } from 'src/utils/formatter-utils'

const LOG = Logger.get('RESULTADO-DETALHADO-ANALISE-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    },
    warning: {
        color: '#ed6c02'
    }
})

type ResultadoDetalhadoAnaliseViewProps = {
    className?: string
    scope: ResultadoDetalhadoAnaliseScope
}
class ResultadoAnaliseViewClass implements FCClassContext<ResultadoDetalhadoAnaliseViewProps> {
    // :: Fields
    scope!: ResultadoDetalhadoAnaliseScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors
    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitShowResults = (results: ShowResultadoDetalhado) => {
        this.scope.onShowResults(results).catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: ResultadoDetalhadoAnaliseViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: ResultadoDetalhadoAnaliseViewProps) {
        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box
                    sx={{
                        p: 4
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        Borderô Resultado
                    </Typography>

                    <Grid
                        container
                        spacing={2}
                        sx={{
                            mb: 2
                        }}
                    >
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Card>
                                <CardContent>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            mb: 1
                                        }}
                                    >
                                        <Grid size={{ xs: 9, sm: 9 }}>
                                            <Typography variant="subtitle2">BORDERÔ</Typography>
                                            <Typography variant="h4">Nº {this.scope.resultado.codigo}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 3 }}>
                                            <Work fontSize="large" sx={{ width: '1.6em', height: '1.6em' }} />
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        container
                                        sx={{
                                            ml: 1,
                                            mb: 1,
                                            mt: 2
                                        }}
                                    >
                                        <Grid size={{ xs: 6, sm: 6 }}>
                                            <Typography variant="caption">Data </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.data &&
                                                    formatDate(this.scope.resultado.data, 'dd/MM/yyyy')}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6, sm: 6 }}>
                                            <Typography variant="caption">Valor </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.valor &&
                                                    formatCurrency(this.scope.resultado.valor)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Card>
                                <CardContent>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            mb: 1
                                        }}
                                    >
                                        <Grid size={{ xs: 9, sm: 9 }}>
                                            <Typography variant="subtitle2">SACADOS</Typography>
                                            <Typography variant="h4">{this.scope.resultado.sacados?.length}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 3 }}>
                                            <Groups fontSize="large" sx={{ width: '1.6em', height: '1.6em' }} />
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        container
                                        sx={{
                                            ml: 1,
                                            mb: 1,
                                            mt: 2
                                        }}
                                    >
                                        <Grid size={{ xs: 4, sm: 4 }}>
                                            <Typography variant="caption">Alerta</Typography>
                                            <Typography
                                                variant="body1"
                                                className={classes.warning}
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.alertaSacado || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4, sm: 4 }}>
                                            <Typography variant="caption">Bloqueio</Typography>
                                            <Typography
                                                variant="body1"
                                                color="error"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.bloqueioSacado || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4, sm: 4 }}>
                                            <Typography variant="caption">Novos Serasa</Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.novosSerasa || '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Card>
                                <CardContent>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            mb: 1
                                        }}
                                    >
                                        <Grid size={{ xs: 9, sm: 9 }}>
                                            <Typography variant="subtitle2">TÍTULOS</Typography>
                                            <Typography variant="h4">
                                                {this.scope.resultado.sacados?.map((s) => s.titulos).flat().length}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 3 }}>
                                            <FileCopy fontSize="large" sx={{ width: '1.6em', height: '1.6em' }} />
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        container
                                        sx={{
                                            ml: 1,
                                            mb: 1,
                                            mt: 2
                                        }}
                                    >
                                        <Grid size={{ xs: 6, sm: 6 }}>
                                            <Typography variant="caption">Alerta</Typography>
                                            <Typography
                                                variant="body1"
                                                className={classes.warning}
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.alertaTitulo || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6, sm: 6 }}>
                                            <Typography variant="caption">Bloqueio</Typography>
                                            <Typography
                                                variant="body1"
                                                color="error"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.bloqueioTitulo || '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Card>
                                <CardContent>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            mb: 1
                                        }}
                                    >
                                        <Grid size={{ xs: 9, sm: 9 }}>
                                            <Typography variant="subtitle2">NF-E</Typography>
                                            <Typography variant="h4">
                                                {this.scope.resultado.sacados?.map((s) => s.notas).flat().length}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 3 }}>
                                            <Description fontSize="large" sx={{ width: '1.6em', height: '1.6em' }} />
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        container
                                        sx={{
                                            ml: 1,
                                            mb: 1,
                                            mt: 2
                                        }}
                                    >
                                        <Grid size={{ xs: 6, sm: 6 }}>
                                            <Typography variant="caption">Alerta</Typography>
                                            <Typography
                                                variant="body1"
                                                className={classes.warning}
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.alertaNfe || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6, sm: 6 }}>
                                            <Typography variant="caption">Bloqueio</Typography>
                                            <Typography
                                                variant="body1"
                                                color="error"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {this.scope.resultado.bloqueioNfe || '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle1">Cedente</Typography>
                            <Divider />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1
                                }}
                            >
                                <ResultIcon
                                    fontSize="large"
                                    sx={{ width: '1.3em', height: '1.3em' }}
                                    resultado={this.scope.resultado.cedente?.resultados}
                                />
                                <Typography
                                    variant="h5"
                                    sx={{
                                        ml: 2
                                    }}
                                >
                                    {this.scope.resultado.cedente?.nome}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <IconButton
                                    sx={{ marginLeft: 7 }}
                                    onClick={() =>
                                        this.emitShowResults({
                                            idOrigem: this.scope.resultado.cedente.id,
                                            origem: 'Cedente',
                                            nome: this.scope.resultado.cedente?.nome,
                                            documento: formatCNPJ(this.scope.resultado.cedente?.cnpjCpf),
                                            resultado: this.scope.resultado.cedente?.resultados
                                        })
                                    }
                                >
                                    <Business fontSize="small" />
                                </IconButton>
                                <Typography variant="body2">
                                    {formatCNPJ(this.scope.resultado.cedente?.cnpjCpf)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>CNPJ/CPF</TableCell>
                                        <TableCell align="center">Títulos</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell align="center">NF-e / XML</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {this.scope.resultado.sacados?.map((sacado, index) => (
                                        <SacadoResultRow
                                            key={index}
                                            sacado={sacado}
                                            emitShowResults={this.emitShowResults}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                                alignItems: 'end',
                                gap: 3,
                                mt: 2,
                                mr: 2,
                                mb: 2
                            }}
                        >
                            <Button variant="outlined" onClick={this.emitOnClose}>
                                Fechar
                            </Button>
                        </Box>
                    </Card>
                </Box>
            </div>
        )
    }
}

export const ResultadoAnaliseView = classToFComponent(ResultadoAnaliseViewClass, React)
export default ResultadoAnaliseView

function SacadoResultRow(props: {
    sacado: ResultadoDetalhadoSacado
    emitShowResults: (results: ShowResultadoDetalhado) => void
}) {
    const { sacado, emitShowResults } = props
    const [collapseState, setCollapseState] = React.useState({ open: false, type: 'TITULOS' } as {
        open: boolean
        type: 'TITULOS' | 'NOTAS'
    })

    const openTitulos = () => {
        const open: boolean = collapseState.open && collapseState.type === 'TITULOS' ? !collapseState.open : true

        setCollapseState({ open: open, type: 'TITULOS' })
    }

    const openNotas = () => {
        const open: boolean = collapseState.open && collapseState.type === 'NOTAS' ? !collapseState.open : true

        setCollapseState({ open: open, type: 'NOTAS' })
    }

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() =>
                            emitShowResults({
                                idOrigem: sacado.id,
                                origem: 'Sacado',
                                nome: sacado.nome,
                                documento: formatCPFOrCNPJ(sacado.cnpjCpf),
                                resultado: sacado.resultados
                            })
                        }
                    >
                        <ResultIcon fontSize="small" resultado={sacado.resultados} />
                    </IconButton>
                    {sacado.nome}
                </TableCell>
                <TableCell>{formatCPFOrCNPJ(sacado.cnpjCpf)}</TableCell>
                <TableCell align="center">
                    <IconButton aria-label="expand row" size="small" onClick={openTitulos}>
                        <ResultIcon fontSize="small" resultadoArray={sacado.titulos?.map((n) => n.resultados) || []} />
                    </IconButton>
                    {sacado.titulos?.length}
                </TableCell>
                <TableCell align="right">
                    {formatCurrency(sacado.titulos?.map((t) => t.valor).reduce((t, v) => t + v) || 0)}
                </TableCell>
                <TableCell align="center">
                    <IconButton aria-label="expand row" size="small" onClick={openNotas}>
                        <ResultIcon fontSize="small" resultadoArray={sacado.notas?.map((n) => n.resultados) || []} />
                    </IconButton>
                    {sacado.notas?.length}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={collapseState.open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            {(collapseState.type === 'TITULOS' && (
                                <TituloResultRow
                                    key={sacado.id}
                                    titulos={sacado.titulos}
                                    notas={sacado.notas}
                                    emitShowResults={(result) =>
                                        (result.idSacado = sacado.id) && emitShowResults(result)
                                    }
                                />
                            )) || (
                                <NotaResultRow
                                    key={sacado.id}
                                    notas={sacado.notas}
                                    emitShowResults={(result) =>
                                        (result.idSacado = sacado.id) && emitShowResults(result)
                                    }
                                />
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    )
}

function TituloResultRow(props: {
    key: number
    titulos?: ResultadoDetalhadoTitulo[]
    notas?: ResultadoDetalhadoNota[]
    emitShowResults: (results: ShowResultadoDetalhado) => void
}) {
    const { key, titulos, notas, emitShowResults } = props

    const getNfe = (titulo: ResultadoDetalhadoTitulo) => {
        const nota = notas?.filter((n) => n.chave === titulo.chaveNfe)
        return nota && nota.length > 0 ? nota[0] : undefined
    }

    return (
        <React.Fragment key={key}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Documento</TableCell>
                        <TableCell>Vencimento</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>NF-e</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {titulos?.map((titulo, i) => {
                        const nfe = getNfe(titulo)

                        return (
                            <TableRow key={i}>
                                <TableCell>
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() =>
                                            emitShowResults({
                                                idOrigem: titulo.id,
                                                origem: 'Título',
                                                nome: titulo.documento,
                                                documento: !titulo.vencimento
                                                    ? ''
                                                    : `Vecimento ${formatDateNoTz(titulo.vencimento)}`,
                                                resultado: titulo.resultados
                                            })
                                        }
                                    >
                                        <ResultIcon fontSize="small" resultado={titulo.resultados} />
                                    </IconButton>
                                    {titulo.documento}
                                </TableCell>
                                <TableCell>{titulo.vencimento && formatDateNoTz(titulo.vencimento)}</TableCell>
                                <TableCell align="right">{titulo.valor && formatCurrency(titulo.valor)}</TableCell>
                                <TableCell>
                                    {nfe && (
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={() =>
                                                emitShowResults({
                                                    idOrigem: nfe.id,
                                                    origem: 'NF-e',
                                                    nome: titulo.chaveNfe,
                                                    documento: '',
                                                    resultado: nfe.resultados
                                                })
                                            }
                                        >
                                            <ResultIcon fontSize="small" resultado={nfe.resultados} />
                                        </IconButton>
                                    )}
                                    {titulo.chaveNfe}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </React.Fragment>
    )
}

function NotaResultRow(props: {
    key: number
    notas?: ResultadoDetalhadoNota[]
    emitShowResults: (results: ShowResultadoDetalhado) => void
}) {
    const { key, notas, emitShowResults } = props

    return (
        <React.Fragment key={key}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Chave NF-e</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {notas?.map((nota, i) => (
                        <TableRow key={i}>
                            <TableCell>
                                <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() =>
                                        emitShowResults({
                                            idOrigem: nota.id,
                                            origem: 'NF-e',
                                            nome: nota.chave,
                                            documento: '',
                                            resultado: nota.resultados
                                        })
                                    }
                                >
                                    <ResultIcon fontSize="small" resultado={nota.resultados} />
                                </IconButton>{' '}
                                {nota.chave}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </React.Fragment>
    )
}

type ResultadoIconProps = {
    resultado?: ResultadoDetalhado
    resultadoArray?: ResultadoDetalhado[]
} & React.ComponentProps<typeof SvgIcon>

const ResultIcon: React.FC<ResultadoIconProps> = ({ resultado, resultadoArray, ...props }) => {
    if (
        (resultado && resultado.bloqueio?.length > 0) ||
        (resultadoArray && resultadoArray.length > 0 && resultadoArray.some((r) => r.bloqueio?.length > 0))
    ) {
        return <ErrorOutline color="error" {...props} />
    }
    if (
        (resultado && resultado.alerta?.length > 0) ||
        (resultadoArray && resultadoArray.length > 0 && resultadoArray.some((r) => r.alerta?.length > 0))
    ) {
        return <WarningAmber color="warning" {...props} />
    }
    return <CheckCircle color="success" {...props} />
}
